// ============================================================================
// Resolver zona waktu ETAMSIOS (#4b-1, Keputusan Q67a) — cermin aturan S14
// backend (services/pdf_service.py::_doc_timezone):
//
//   * Entitas ber-store_id  -> stores.timezone milik store itu.
//   * Entitas milik region  -> timezone BULAT SUARA seluruh store region itu;
//     kalau region punya >1 zona atau ada store tanpa timezone -> null.
//   * null berarti: tampilkan jam TANPA label zona (Keputusan Q67 —
//     JANGAN jatuh ke WIB, JANGAN menebak dari zona perangkat).
//
// Sumber data: GET /stores (kolom `timezone` ikut dikirim sejak #4b-1).
// Cache modul sekali muat — daftar store praktis statis selama sesi.
// ============================================================================

import { api } from "./api";
import { formatDateTime } from "./format";

let _stores = null;
let _inflight = null;

/** Muat daftar store (sekali per sesi). Gagal jaringan → [] dan akan dicoba
 *  ulang pada panggilan berikutnya; halaman tetap jalan tanpa label zona. */
export async function loadStores() {
  if (_stores) return _stores;
  if (!_inflight) {
    _inflight = api
      // ⛔ `untuk=zona-waktu` WAJIB, dan alasannya bukan kelengkapan data.
      //    Sejak P-1 Tahap 2, `/stores` tanpa parameter DIJEPIT WILAYAH.
      //    Peta zona ini dipakai 11 layar purchasing, dan pemakai utamanya
      //    Admin Purchasing yang memang lintas region — tanpa parameter ini
      //    label WIB/WITA HILANG untuk cabang di luar wilayah pemanggil,
      //    diam-diam, tanpa satu pun galat.
      .get("/stores", { params: { untuk: "zona-waktu" } })
      .then((r) => {
        _stores = Array.isArray(r.data) ? r.data : [];
        return _stores;
      })
      .catch(() => {
        _inflight = null; // biar dicoba lagi nanti
        return [];
      });
  }
  return _inflight;
}

function _clean(tz) {
  return typeof tz === "string" ? tz.trim() : "";
}

/** IANA timezone untuk satu store, atau null (store tak dikenal / kolom
 *  kosong — sistem tidak menebak). Butuh loadStores() sudah selesai. */
export function tzForStore(storeId) {
  if (!_stores || !storeId) return null;
  const s = _stores.find((x) => x.id === storeId);
  const tz = _clean(s?.timezone);
  return tz || null;
}

/** IANA timezone untuk satu region: bulat suara seluruh store region itu.
 *  Region tanpa store, ada store tanpa timezone, atau >1 zona → null. */
export function tzForRegion(regionId) {
  if (!_stores || !regionId) return null;
  const rows = _stores.filter((x) => x.region_id === regionId);
  if (rows.length === 0) return null;
  const zones = new Set(rows.map((r) => _clean(r.timezone)));
  if (zones.has("") || zones.size !== 1) return null;
  return zones.values().next().value;
}

/** Hook ringan: pastikan cache termuat lalu paksa render ulang sekali,
 *  supaya pemanggilan tzForRegion/tzForStore sinkron di render berikutnya. */
import { useEffect, useState } from "react";
export function useStoreTimezones() {
  const [ready, setReady] = useState(!!_stores);
  useEffect(() => {
    let alive = true;
    loadStores().then(() => {
      if (alive) setReady(true);
    });
    return () => {
      alive = false;
    };
  }, []);
  return { ready, tzForStore, tzForRegion };
}

// ============================================================================
// T93 (Titipan auditor 1 Nov 2026) — jam di layar staf menurut zona CABANG PESANAN + label (WIB/WITA), pola
// Ruang Pesanan. Layar FA Surabaya dibuka Owner di WITA dulu menulis "11.10" untuk konfirmasi 10.10 WIB tanpa
// label: jam ikut zona peramban. Cabang (store) dulu, lalu wilayah bila bulat suara; tidak dikenal → tanpa label
// (Q67: tidak menebak). Halaman pemakai WAJIB memanggil `useStoreTimezones()` supaya cache termuat.
// ============================================================================

/** IANA timezone pesanan: cabang dulu, lalu wilayah (bulat suara), atau null. */
export function zonaPesanan(storeId, regionId) {
  return tzForStore(storeId) || tzForRegion(regionId) || null;
}

/** "01 Nov 2026 10.10 WIB" — jam menurut zona cabang pesanan. */
export function jamPesanan(d, storeId, regionId) {
  return formatDateTime(d, zonaPesanan(storeId, regionId));
}
